import React, { useEffect, useRef, useState } from "react";
import { Mic, MicOff, X, Bot, Volume2, VolumeX, Send, Sparkles } from "lucide-react";
import api from "@/lib/api";

// Hands-free voice autopilot using the browser Web Speech API (SpeechRecognition + speechSynthesis).
// Transcribes speech, sends it to the org-grounded AI copilot, then speaks the answer aloud.
export function VoiceCopilot({ orgId }) {
  const [open, setOpen] = useState(false);
  const [listening, setListening] = useState(false);
  const [muted, setMuted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState("");
  const [msgs, setMsgs] = useState([
    { role: "assistant", content: "Hi, I'm your Sitepar autopilot. Tap the mic and ask me anything about your business — like \"Which projects are delayed?\" or \"What payments are overdue?\"" },
  ]);
  const recRef = useRef(null);
  const supported = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const scrollRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [msgs, open]);

  const speak = (t) => {
    if (muted || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(t.replace(/[#*`_]/g, ""));
    u.rate = 1.05; u.pitch = 1;
    window.speechSynthesis.speak(u);
  };

  const ask = async (q) => {
    if (!q.trim()) return;
    setMsgs((m) => [...m, { role: "user", content: q }]);
    setText(""); setBusy(true);
    try {
      const { data } = await api.post(`/organizations/${orgId}/ai/ask`, { message: q });
      setMsgs((m) => [...m, { role: "assistant", content: data.reply }]);
      speak(data.reply);
    } catch (e) {
      const err = "I couldn't reach the AI service just now. Please try again.";
      setMsgs((m) => [...m, { role: "assistant", content: err }]);
    } finally { setBusy(false); }
  };

  const toggleMic = () => {
    if (!supported) return;
    if (listening) { recRef.current?.stop(); return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = "en-IN"; rec.interimResults = false; rec.maxAlternatives = 1;
    rec.onstart = () => setListening(true);
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    rec.onresult = (e) => { const t = e.results[0][0].transcript; ask(t); };
    recRef.current = rec;
    window.speechSynthesis?.cancel();
    rec.start();
  };

  return (
    <>
      {!open && (
        <button data-testid="voice-launcher" onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-sp-orange hover:bg-sp-orangedark shadow-xl shadow-sp-orange/30 flex items-center justify-center text-white transition-transform hover:scale-105">
          <Sparkles className="h-6 w-6" />
        </button>
      )}
      {open && (
        <div data-testid="voice-panel" className="fixed bottom-6 right-6 z-50 w-[92vw] max-w-sm rounded-2xl bg-white border border-slate-200 shadow-2xl overflow-hidden flex flex-col" style={{ height: "min(560px, 80vh)" }}>
          <div className="flex items-center justify-between px-4 py-3 bg-sp-navy text-white">
            <div className="flex items-center gap-2"><div className="h-8 w-8 rounded-lg bg-sp-orange flex items-center justify-center"><Bot className="h-4 w-4" /></div>
              <div><div className="text-sm font-semibold">Sitepar Autopilot</div><div className="text-[10px] text-white/60">Voice-enabled business copilot</div></div></div>
            <div className="flex items-center gap-1">
              <button onClick={() => setMuted((m) => !m)} data-testid="voice-mute" className="p-1.5 rounded hover:bg-white/10">{muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}</button>
              <button onClick={() => { setOpen(false); window.speechSynthesis?.cancel(); recRef.current?.stop(); }} data-testid="voice-close" className="p-1.5 rounded hover:bg-white/10"><X className="h-4 w-4" /></button>
            </div>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto sp-scroll p-4 space-y-3 bg-slate-50">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm ${m.role === "user" ? "bg-sp-orange text-white rounded-br-sm" : "bg-white border border-slate-200 text-sp-navy rounded-bl-sm"}`}>{m.content}</div>
              </div>
            ))}
            {busy && <div className="flex justify-start"><div className="bg-white border border-slate-200 rounded-2xl px-3.5 py-2.5 text-sm text-slate-400">Thinking…</div></div>}
          </div>
          <div className="p-3 border-t border-slate-200 bg-white">
            {listening && <div className="text-center text-xs text-sp-orange mb-2 animate-pulse">Listening… speak now</div>}
            <div className="flex items-center gap-2">
              <button onClick={toggleMic} disabled={!supported} title={supported ? "Speak" : "Voice not supported in this browser"} data-testid="voice-mic"
                className={`h-10 w-10 shrink-0 rounded-full flex items-center justify-center text-white transition-colors ${listening ? "bg-red-500 animate-pulse" : "bg-sp-navy hover:bg-sp-navy/90"} ${!supported ? "opacity-40" : ""}`}>
                {listening ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
              </button>
              <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask(text)}
                data-testid="voice-input" placeholder="Type or speak your question…" className="flex-1 h-10 rounded-full border border-slate-200 px-4 text-sm outline-none focus:border-sp-orange" />
              <button onClick={() => ask(text)} disabled={busy || !text.trim()} data-testid="voice-send" className="h-10 w-10 shrink-0 rounded-full bg-sp-orange hover:bg-sp-orangedark disabled:opacity-40 flex items-center justify-center text-white"><Send className="h-4 w-4" /></button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
